import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

const SESSION_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days in ms

const isSessionExpired = () => {
  const loginTime = localStorage.getItem('wd_login_time');
  if (!loginTime) return true;
  return Date.now() - parseInt(loginTime, 10) > SESSION_DURATION_MS;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('wd_user')); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [presenceMap, setPresenceMap] = useState({});

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem('wd_token');
    if (!token) { setLoading(false); return; }

    // ── Check 7-day session expiry on every load ──
    if (isSessionExpired()) {
      logout();
      setLoading(false);
      return;
    }

    try {
      const { data } = await api.get('/api/auth/me');
      setUser(data.user);
      localStorage.setItem('wd_user', JSON.stringify(data.user));
    } catch {
      logout();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  // ─── Silent Token Refresh every 50 minutes ───────────────────────────────
  // Firebase idToken expires every 1 hour. We refresh it every 50 min silently
  // so the user stays logged in for the full 7-day session.
  useEffect(() => {
    const silentRefresh = async () => {
      const rfToken = localStorage.getItem('wd_refresh_token');
      const loginTime = localStorage.getItem('wd_login_time');
      if (!rfToken || !loginTime) return;

      // Stop refreshing if 7-day session has expired
      if (isSessionExpired()) {
        logout();
        return;
      }

      try {
        const { data } = await api.post('/api/auth/refresh-token', { refreshToken: rfToken });
        if (data.success) {
          localStorage.setItem('wd_token', data.token);
          if (data.refreshToken) localStorage.setItem('wd_refresh_token', data.refreshToken);
          console.log('[Session] Token silently refreshed.');
        }
      } catch (err) {
        console.warn('[Session] Silent token refresh failed:', err.message);
      }
    };

    // Run every 50 minutes (3,000,000 ms)
    const interval = setInterval(silentRefresh, 50 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Real-time Presence EventSource Stream (WhatsApp-like real-time)
  useEffect(() => {
    if (!user) {
      setPresenceMap({});
      return;
    }

    const hasUsersView = user.role === 'super_admin' || user.role === 'admin';
    if (!hasUsersView) return;

    const token = localStorage.getItem('wd_token');
    if (!token) return;

    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const url = `${baseURL}/api/admin/users/presence?token=${token}`;

    const eventSource = new EventSource(url);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const map = {};
        data.forEach(item => {
          map[item.id] = item;
        });
        setPresenceMap(map);
      } catch (err) {
        console.error('Error parsing presence SSE event', err);
      }
    };

    eventSource.onerror = (err) => {
      console.error('EventSource connection error, closing...', err);
      eventSource.close();
    };

    return () => {
      eventSource.close();
    };
  }, [user]);

  const isUserOnline = useCallback((userId) => {
    if (user && userId === user.id) return true; // Current user is always online
    const presence = presenceMap[userId];
    if (!presence || !presence.last_active_at) return false;
    const diff = new Date() - new Date(presence.last_active_at);
    return diff < 3 * 60 * 1000; // 3 minutes presence window
  }, [presenceMap, user]);

  const login = async (email, password, portalType = 'admin') => {
    const { data } = await api.post('/api/auth/login', { email, password, portalType });
    localStorage.setItem('wd_token', data.token);
    localStorage.setItem('wd_user', JSON.stringify(data.user));
    localStorage.setItem('wd_login_time', Date.now().toString()); // ← 7-day timer starts
    if (data.refreshToken) {
      localStorage.setItem('wd_refresh_token', data.refreshToken); // ← for silent renewal
    }
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem('wd_token');
    localStorage.removeItem('wd_user');
    localStorage.removeItem('wd_login_time');    // ← clear session timer
    localStorage.removeItem('wd_refresh_token'); // ← clear refresh token
    setUser(null);
  };

  const can = (rolesOrPermissions) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;

    const userPermissions = Array.isArray(user.permissions) ? user.permissions : [];
    const items = Array.isArray(rolesOrPermissions) ? rolesOrPermissions : [rolesOrPermissions];

    const roleChecks = items.filter(item => typeof item === 'string' && !item.includes('.'));
    const permissionChecks = items.filter(item => typeof item === 'string' && item.includes('.'));

    if (roleChecks.length > 0 && roleChecks.includes(user.role)) {
      return true;
    }

    const hasPermission = permissionChecks.some(item => userPermissions.includes(item));

    if (!hasPermission && process.env.NODE_ENV === 'development' && permissionChecks.length > 0) {
      console.log('Permission check:', { checking: permissionChecks, userPermissions, userRole: user.role });
    }

    return hasPermission;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, can, presenceMap, isUserOnline }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
